import { useState, useEffect } from 'react';
import { DollarSign, Shield, ShoppingCart, Leaf, Eye, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import ContentEditor from '@/components/admin/ContentEditor';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

const WhyBuyFromUs = () => {
  const { isAdmin } = useAuth();
  const [benefits, setBenefits] = useState([
    {
      icon: DollarSign,
      title: "מודלים חכמים לחיסכון",
      description: "שלבו בין מחירי חיסול חסרי תקדים לכוח הקנייה של קבוצות רכישה. השיגו מוצרים איכותיים במחירים נמוכים משמעותית ממחיר השוק."
    },
    {
      icon: Shield,
      title: "איכות ובטיחות",
      description: "כל מוצר, בין אם במכירה רגילה, חיסול או קבוצת רכישה, עובר בדיקה, מדידה וסימון להבטחת איכות ועמידה בתקנים."
    },
    {
      icon: ShoppingCart,
      title: "קנייה חכמה וגמישה",
      description: "בחרו את הדרך המתאימה לכם: רכישה מיידית, הזדמנות בחיסול מלאי, או הצטרפות לקבוצת רכישה לחיסכון מקסימלי."
    },
    {
      icon: Leaf,
      title: "תרומה לסביבה",
      description: "רכישת עודפים וחיסולי מלאי מצמצמת ייצור מיותר, מונעת הטמנת פסולת ושומרת על משאבי כדור הארץ."
    },
    {
      icon: Eye,
      title: "שקיפות מלאה",
      description: "מידע בזמן אמת על סטטוס קבוצות רכישה, כמויות במלאי חיסול, ותאריכי יעד ברורים לכל עסקה."
    },
    {
      icon: Search,
      title: "מציאת מוצרים",
      description: "לא מצאתם את המוצר? שלחו לנו מפרט וכמות - המערכת שלנו תאתר עבורכם פתרון דרך רשת הספקים והחיסולים שלנו."
    }
  ]);

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_contents')
        .select('*')
        .eq('page_name', 'homepage')
        .eq('section_name', 'WhyBuyFromUs')
        .order('order_index');

      if (error) throw error;

      if (data && data.length > 0) {
        const contentBenefits = data.map((item) => {
          const parsedContent = JSON.parse(item.content);
          const iconMap: Record<string, any> = {
            DollarSign,
            Shield,
            ShoppingCart,
            Leaf,
            Eye,
            Search
          };

          return {
            icon: iconMap[parsedContent.icon] || DollarSign,
            title: parsedContent.title,
            description: parsedContent.description
          };
        });
        setBenefits(contentBenefits);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground hebrew-heading">
              למה לקנות אצלנו?
            </h2>
            {isAdmin && (
              <ContentEditor componentType="WhyBuyFromUs" onSave={loadContent} />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-card rounded-2xl p-8 shadow-elegant hover:shadow-glow transition-smooth border border-border flex flex-col">
              <div className="w-16 h-16 bg-lime/10 rounded-xl flex items-center justify-center mb-6">
                <benefit.icon className="h-8 w-8 text-lime" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-4 hebrew-heading">
                {benefit.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed hebrew-body flex-grow">
                {benefit.description}
              </p>

              {/* Special CTA for the Product Sourcing benefit */}
              {benefit.title === "מציאת מוצרים" && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <Link
                    to="/contact?tab=special-search"
                    className="inline-flex items-center text-lime hover:text-lime/80 font-medium underline underline-offset-4 transition-colors text-right"
                  >
                    שלח בקשה &gt;
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyBuyFromUs;